// RegisterComponent.tsx
"use client";

import { useSchoolRegisterMutation, useStudentRegisterMutation, useStudentRegisterValidateMutation } from "@/api/auth/authPublicApi";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { toast } from "sonner";

interface RegisterComponentProps {
  onToggle: () => void;
  onCompanyRegisterSuccess?: () => void;
  onStudentRegisterSuccess?: () => void;
}

const RegisterComponent: React.FC<RegisterComponentProps> = ({
  onToggle,
  onCompanyRegisterSuccess,
  onStudentRegisterSuccess,
}) => {
  const t = useTranslations();

  const [registerMode, setRegisterMode] = useState<"student" | "company">(
    "student"
  );

  // Student registration form state
  const [studentInviteCode, setStudentInviteCode] = useState("");
  const [studentFirstname, setStudentFirstname] = useState("");
  const [studentLastname, setStudentLastname] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [studentPassword, setStudentPassword] = useState("");
  const [studentAgree, setStudentAgree] = useState(false);
  const [isStudentRegistered, setIsStudentRegistered] = useState(false);

  const [studentFieldErrors, setStudentFieldErrors] = useState<{
    invite_code?: boolean;
    email?: boolean;
    password?: boolean;
  }>({});

  // Company / school registration form state
  const [schoolName, setSchoolName] = useState("");
  const [schoolShortDescription, setSchoolShortDescription] = useState("");
  const [schoolFullDescription, setSchoolFullDescription] = useState("");
  const [schoolWebsite, setSchoolWebsite] = useState("");
  const [schoolPhone, setSchoolPhone] = useState("");
  const [schoolEmail, setSchoolEmail] = useState("");

  const [managerFirstname, setManagerFirstname] = useState("");
  const [managerLastname, setManagerLastname] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [managerPhone, setManagerPhone] = useState("");
  const [managerPassword, setManagerPassword] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{
    email?: boolean;
    phone?: boolean;
  }>({});

  const [schoolRegister, { isLoading }] = useSchoolRegisterMutation();
  const [studentRegisterValidate] = useStudentRegisterValidateMutation();
  const [studentRegister, { isLoading: isStudentLoading }] = useStudentRegisterMutation();

  const handleCompanySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFieldErrors({});

    try {
      await schoolRegister({
        school: {
          name: schoolName,
          short_description: schoolShortDescription,
          full_description: schoolFullDescription,
          website: schoolWebsite,
          phone: schoolPhone,
          email: schoolEmail,
        },
        user: {
          firstname: managerFirstname,
          lastname: managerLastname,
          email: managerEmail,
          phone: managerPhone,
          password: managerPassword,
        },
      }).unwrap();

      if (onCompanyRegisterSuccess) {
        onCompanyRegisterSuccess();
      }
    } catch (e: any) {
      console.error("School/company registration failed", e);

      const data = e?.data;
      const messages: string[] = [];
      const fieldErr: { email?: boolean; phone?: boolean } = {};

      // Ожидаемый формат:
      // {
      //   "status": "error",
      //   "status_code": 2101,
      //   "errors": {
      //     "user.email": ["The user.email has already been taken."],
      //     "user.phone": ["The user.phone has already been taken."]
      //   }
      // }
      if (data && typeof data === "object" && "errors" in data) {
        const errorsObj = (data as { errors?: Record<string, string[]> }).errors;

        if (errorsObj && typeof errorsObj === "object") {
          Object.entries(errorsObj).forEach(([field, msgs]) => {
            if (!Array.isArray(msgs)) return;

            msgs.forEach((msg) => {
              if (field === "user.email") {
                fieldErr.email = true;
                messages.push("Пользователь с таким email уже существует");
              } else if (field === "user.phone") {
                fieldErr.phone = true;
                messages.push(
                  "Пользователь с таким номером телефона уже существует"
                );
              } else {
                // Для прочих полей временно показываем оригинальный текст
                messages.push(msg);
              }
            });
          });
        }

        if (messages.length > 0) {
          setFieldErrors(fieldErr);
          // Show each error message via toast
          Array.from(new Set(messages)).forEach((msg) => toast.error(msg));
          return;
        }
      }

      if (typeof data === "string") {
        toast.error(data);
      } else {
        toast.error("Ошибка при регистрации компании. Попробуйте позже.");
      }
    }
  };

  const handleStudentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStudentFieldErrors({});

    const payload = {
      invite_code: studentInviteCode,
      firstname: studentFirstname,
      lastname: studentLastname,
      patronymic: null,
      phone: null,
      email: studentEmail,
      password: studentPassword,
      recaptcha_token: null, // TODO: Add reCAPTCHA if required
      agree: studentAgree ? 1 : 0,
    };

    try {
      // Step 1: Validate
      await studentRegisterValidate(payload).unwrap();

      // Step 2: Submit (only if validation passed)
      const result = await studentRegister(payload).unwrap();

      // Registration successful - show success screen
      setIsStudentRegistered(true);

      // Optionally call the callback
      if (result.access_token && onStudentRegisterSuccess) {
        onStudentRegisterSuccess();
      }
    } catch (e: any) {
      console.error("Student registration failed", e);

      const data = e?.data;
      const messages: string[] = [];
      const fieldErr: { invite_code?: boolean; email?: boolean; password?: boolean } = {};

      if (data && typeof data === "object" && "errors" in data) {
        const errorsObj = (data as { errors?: Record<string, string[]> }).errors;

        if (errorsObj && typeof errorsObj === "object") {
          Object.entries(errorsObj).forEach(([field, msgs]) => {
            if (!Array.isArray(msgs)) return;

            msgs.forEach((msg) => {
              if (field === "invite_code") {
                fieldErr.invite_code = true;
                messages.push(t("error-invalid-invite-code"));
              } else if (field === "email") {
                fieldErr.email = true;
                messages.push(t("error-email-already-exists"));
              } else if (field === "password") {
                fieldErr.password = true;
                messages.push(msg);
              } else {
                messages.push(msg);
              }
            });
          });
        }

        if (messages.length > 0) {
          setStudentFieldErrors(fieldErr);
          Array.from(new Set(messages)).forEach((msg) => toast.error(msg));
          return;
        }
      }

      if (typeof data === "string") {
        toast.error(data);
      } else {
        toast.error(t("error-registration-failed"));
      }
    }
  };

  return (
    <div className="w-full">
      {/* Success screen after registration */}
      {isStudentRegistered ? (
        <div className="flex flex-col items-center justify-center h-[420px] text-center">
          {/* Green checkmark icon */}
          <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Success title */}
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t("label-success-register-title")}
          </h2>

          {/* Success description */}
          <p className="text-gray-500 text-sm mb-6">
            {t("label-success-register-description")}
          </p>

          {/* Login button */}
          <button
            type="button"
            onClick={onToggle}
            className="px-8 py-2 rounded-lg font-medium text-blue-600 border border-blue-600 hover:bg-blue-50 transition-all"
          >
            {t("btn-entrace")}
          </button>
        </div>
      ) : (
        <>
          <h2 className="text-[14px] font-bold text-gray-800 mb-2 text-center">
            {t("btn-register")}
          </h2>
          <p className="text-gray-500 text-sm text-center mb-4">
            {t("label-register-subtitle")}
          </p>

          {/* Switch between student / company moved to bottom links */}

          {/* Student registration (current implementation) */}
          {registerMode === "student" && (
            <form
              className="space-y-4 h-[420px] overflow-y-auto p-1"
              onSubmit={handleStudentSubmit}
            >

              <input
                type="text"
                autoComplete="off"
                required
                value={studentInviteCode}
                onChange={(e) => setStudentInviteCode(e.target.value)}
                placeholder={t("label-invite-code")}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${studentFieldErrors.invite_code
                  ? "border-red-400 ring-1 ring-red-300"
                  : "border-gray-300"
                  }`}
                disabled={isStudentLoading}
              />
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <input
                  type="text"
                  autoComplete="family-name"
                  required
                  value={studentLastname}
                  onChange={(e) => setStudentLastname(e.target.value)}
                  placeholder={t("label-lastname")}
                  className="w-full sm:w-1/2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isStudentLoading}
                />
                <input
                  type="text"
                  autoComplete="given-name"
                  required
                  value={studentFirstname}
                  onChange={(e) => setStudentFirstname(e.target.value)}
                  placeholder={t("label-firstname")}
                  className="w-full sm:w-1/2 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isStudentLoading}
                />
              </div>
              <input
                type="email"
                autoComplete="email"
                required
                value={studentEmail}
                onChange={(e) => setStudentEmail(e.target.value)}
                placeholder={t("label-email")}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${studentFieldErrors.email
                  ? "border-red-400 ring-1 ring-red-300"
                  : "border-gray-300"
                  }`}
                disabled={isStudentLoading}
              />
              <div className="relative">
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={studentPassword}
                  onChange={(e) => setStudentPassword(e.target.value)}
                  placeholder={t("label-password")}
                  className={`w-full p-3 border rounded-lg pe-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${studentFieldErrors.password
                    ? "border-red-400 ring-1 ring-red-300"
                    : "border-gray-300"
                    }`}
                  disabled={isStudentLoading}
                />
              </div>

              <div className="flex items-start space-x-3 mt-4">
                <input
                  type="checkbox"
                  id="student-agree"
                  checked={studentAgree}
                  onChange={(e) => setStudentAgree(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={isStudentLoading}
                />
                <label htmlFor="student-agree" className="text-gray-600 text-sm">
                  {t.rich("label-register-policy-agreement", {
                    terms: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                    privacy: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                  })}
                </label>
              </div>

              <button
                type="submit"
                disabled={isStudentLoading || !studentAgree}
                className="w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md"
              >
                {isStudentLoading ? t("label-loading") : t("btn-send")}
              </button>

              <div className="flex items-center justify-between mt-4">
                <button
                  type="button"
                  onClick={onToggle}
                  className="text-blue-600 hover:underline text-sm"
                >
                  {t("btn-entrace")}
                </button>

                {/* <button
                  type="button"
                  onClick={() => setRegisterMode("company")}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Компания / школа
                </button> */}
              </div>
            </form>
          )}

          {/* Company / school registration */}
          {registerMode === "company" && (
            <div className="space-y-4">

              <form
                className="space-y-2 h-[420px] overflow-y-auto px-1 flex flex-col justify-between"
                onSubmit={handleCompanySubmit}
              >
                <h3 className="text-sm font-semibold text-gray-700">
                  Данные школы / компании
                </h3>

                <input
                  type="text"
                  autoComplete="organization"
                  required
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder={t("placeholder-school-name")}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />

                <h3 className="text-sm font-semibold text-gray-700 pt-2">
                  Контактное лицо
                </h3>

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={managerFirstname}
                    onChange={(e) => setManagerFirstname(e.target.value)}
                    placeholder={t("placeholder-firstname")}
                    className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isLoading}
                    required
                  />
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={managerLastname}
                    onChange={(e) => setManagerLastname(e.target.value)}
                    placeholder={t("placeholder-lastname")}
                    className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <input
                    type="email"
                    autoComplete="email"
                    value={managerEmail}
                    onChange={(e) => setManagerEmail(e.target.value)}
                    placeholder={t("placeholder-manager-email")}
                    className={`w-full sm:w-1/2 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.email
                      ? "border-red-400 ring-1 ring-red-300"
                      : "border-gray-300"
                      }`}
                    disabled={isLoading}
                    required
                  />
                  {/* <input
                    type="tel"
                    value={managerPhone}
                    onChange={(e) => setManagerPhone(e.target.value)}
                    placeholder={t("placeholder-manager-phone")}
                    className={`w-1/2 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.phone
                      ? "border-red-400 ring-1 ring-red-300"
                      : "border-gray-300"
                      }`}
                    disabled={isLoading}
                    required
                  /> */}
                </div>

                <div className="relative">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={managerPassword}
                    onChange={(e) => setManagerPassword(e.target.value)}
                    placeholder={t("label-password")}
                    className="w-full p-2 border border-gray-300 rounded-lg pe-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isLoading}
                    required
                  />
                </div>

                <p className="text-xs text-gray-500">
                  {t.rich("label-register-policy-agreement", {
                    terms: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                    privacy: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                  })}
                </p>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {isLoading ? t("label-loading") : t("btn-send")}
                </button>

                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    onClick={onToggle}
                    className="text-blue-600 hover:underline text-sm"
                    disabled={isLoading}
                  >
                    {t("btn-entrace")}
                  </button>

                  {/* Переключатель обратно в режим студента */}
                  <button
                    type="button"
                    onClick={() => setRegisterMode("student")}
                    className="text-blue-600 hover:underline text-sm"
                    disabled={isLoading}
                  >
                    Студент
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegisterComponent;
